"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  SimulationResult,
  runMonteCarloSimulation,
} from "@/lib/simulation";
import {
  saveToLocalStorage,
  loadFromLocalStorage,
} from "@/lib/storage";
import {
  useQueryState,
  parseAsInteger,
  parseAsFloat,
} from "nuqs";

// シミュレーション回数（内部パラメータ）
const DEFAULT_SIMULATION_RUNS = 100000;

// 保存されたシミュレーション結果の型
export interface SavedSimulation {
  id: string;
  name: string;
  date: Date;
  params: {
    investmentAmount: number;
    initialInvestment: number;
    investmentPeriod: number;
    returnRate: number;
    volatility: number;
    targetAmount: number;
  };
  result: SimulationResult;
}

interface SimulationContextType {
  // 入力パラメータ
  investmentAmount: number;
  setInvestmentAmount: (amount: number | null) => void;
  initialInvestment: number;
  setInitialInvestment: (amount: number | null) => void;
  investmentPeriod: number;
  setInvestmentPeriod: (period: number | null) => void;
  returnRate: number;
  setReturnRate: (rate: number | null) => void;
  volatility: number;
  setVolatility: (volatility: number | null) => void;
  targetAmount: number;
  setTargetAmount: (amount: number | null) => void;

  // シミュレーション状態
  isLoading: boolean;
  simulationResult: SimulationResult | null;

  // 保存されたシミュレーション
  savedSimulations: SavedSimulation[];
  selectedSimulationIds: string[];
  setSelectedSimulationIds: (ids: string[]) => void;

  // アクション
  runSimulation: (overrideSchedule?: number[]) => void;
  resetSimulation: () => void;
  saveCurrentSimulation: (name: string) => void;
  deleteSimulation: (id: string) => void;
  clearSelectedSimulations: () => void;
  importFromLifePlan: () => any;
  investmentSchedule: number[] | null;
  setInvestmentSchedule: (schedule: number[] | null) => void;
}

const SimulationContext = createContext<SimulationContextType | undefined>(
  undefined
);

export function SimulationProvider({ children }: { children: ReactNode }) {
  // 入力パラメータの状態 (nuqsを使用)
  const [investmentAmount, setInvestmentAmount] = useQueryState(
    "amount",
    parseAsInteger.withDefault(10000)
  );
  const [initialInvestment, setInitialInvestment] = useQueryState(
    "initial",
    parseAsInteger.withDefault(0)
  );
  const [investmentPeriod, setInvestmentPeriod] = useQueryState(
    "period",
    parseAsInteger.withDefault(10)
  );
  const [returnRate, setReturnRate] = useQueryState(
    "return",
    parseAsFloat.withDefault(7)
  );
  const [volatility, setVolatility] = useQueryState(
    "risk",
    parseAsFloat.withDefault(15)
  );
  const [targetAmount, setTargetAmount] = useQueryState(
    "target",
    parseAsInteger.withDefault(1500000)
  );

  // 投資スケジュール（配列の場合）
  const [investmentSchedule, setInvestmentSchedule] = useState<number[] | null>(null);

  // シミュレーション状態
  const [isLoading, setIsLoading] = useState(false);
  const [simulationResult, setSimulationResult] =
    useState<SimulationResult | null>(null);

  // 保存されたシミュレーション
  const [savedSimulations, setSavedSimulations] = useState<SavedSimulation[]>(
    []
  );
  const [selectedSimulationIds, setSelectedSimulationIds] = useState<string[]>(
    []
  );

  // 初期ロード：ローカルストレージから保存されたシミュレーションを読み込む
  useEffect(() => {
    const loadedData = loadFromLocalStorage("riskSimulationData");
    if (loadedData && Array.isArray(loadedData)) {
      // Date型を復元
      const simulations = loadedData.map((sim: any) => ({
        ...sim,
        date: new Date(sim.date),
      }));
      setSavedSimulations(simulations);
    }
  }, []);

  // 保存時の副作用：savedSimulationsが変更されたらローカルストレージに保存
  useEffect(() => {
    if (savedSimulations.length > 0) {
      saveToLocalStorage("riskSimulationData", savedSimulations);
    }
  }, [savedSimulations]);
  
  // ライフプランからデータをインポートする関数
  const importFromLifePlan = () => {
      const lifePlanData = loadFromLocalStorage("lifePlanData");
      if (lifePlanData) {
          // Dynamic import to avoid circular dependencies if possible, or just standard import
          // Since getLifePlanSimulationParams is a pure function, we can import it.
          // However, we need to import it at the top of the file usually.
          // For now, I will assume I added the import statement.
          // Note: need to adding import to top of file in next step.
          return lifePlanData;
      }
      return null;
  };

  // シミュレーションを実行する関数
  const runSimulation = (overrideSchedule?: number[]) => {
    setIsLoading(true);

    // 非同期処理として実行（UIをブロックしないため）
    setTimeout(() => {
      try {
        const scheduleToUse = overrideSchedule || investmentSchedule;
        
        // 配列（スケジュール）がある場合はそれを使用、なければ固定額を使用
        const investmentInput = scheduleToUse || investmentAmount;

        const result = runMonteCarloSimulation(
          investmentInput,
          initialInvestment,
          investmentPeriod,
          returnRate,
          volatility,
          DEFAULT_SIMULATION_RUNS,
          targetAmount
        );

        // シミュレーション結果にパラメータ情報を追加
        setSimulationResult({
          ...result,
          params: {
            investmentAmount: typeof investmentInput === 'number' ? investmentInput : 0, 
            initialInvestment,
            investmentPeriod,
            returnRate,
            volatility,
            targetAmount,
            investmentSchedule: Array.isArray(investmentInput) ? investmentInput : undefined
          },
        });
      } catch (error) {
        console.error("error in runSimulation:", error);
      } finally {
        setIsLoading(false);
      }
    }, 0);
  };

  // シミュレーション結果をリセットする関数
  const resetSimulation = () => {
    setSimulationResult(null);
    setInvestmentSchedule(null); // スケジュールもリセット
  };

  // 現在のシミュレーション結果を保存する関数
  const saveCurrentSimulation = (name: string) => {
    if (!simulationResult) return;

    const newSavedSimulation: SavedSimulation = {
      id: Date.now().toString(),
      name,
      date: new Date(),
      params: {
        investmentAmount: simulationResult.params.investmentAmount,
        initialInvestment,
        investmentPeriod,
        returnRate,
        volatility,
        targetAmount,
      },
      result: simulationResult,
    };

    setSavedSimulations((prev) => [...prev, newSavedSimulation]);
  };

  // 保存されたシミュレーションを削除する関数
  const deleteSimulation = (id: string) => {
    setSavedSimulations((prev) => prev.filter((sim) => sim.id !== id));
    setSelectedSimulationIds((prev) => prev.filter((simId) => simId !== id));
    // localStorageへの保存はuseEffectが担当
    if (savedSimulations.length === 1 && savedSimulations[0].id === id) {
       saveToLocalStorage("riskSimulationData", []); 
    }
  };

  // 選択されたシミュレーションをクリアする関数
  const clearSelectedSimulations = () => {
    setSelectedSimulationIds([]);
  };

  const value = {
    investmentAmount,
    setInvestmentAmount,
    initialInvestment,
    setInitialInvestment,
    investmentPeriod,
    setInvestmentPeriod,
    returnRate,
    setReturnRate,
    volatility,
    setVolatility,
    targetAmount,
    setTargetAmount,
    isLoading,
    simulationResult,
    savedSimulations,
    selectedSimulationIds,
    setSelectedSimulationIds,
    runSimulation,
    resetSimulation,
    saveCurrentSimulation,
    deleteSimulation,
    clearSelectedSimulations,
    importFromLifePlan, // Exposed
    investmentSchedule, // Exposed state
    setInvestmentSchedule, // Exposed setter
  };

  return (
    <SimulationContext.Provider value={value}>
      {children}
    </SimulationContext.Provider>
  );
}

export function useSimulation() {
  const context = useContext(SimulationContext);
  if (context === undefined) {
    throw new Error("useSimulation must be used within a SimulationProvider");
  }
  return context;
}
